<template>
  <div class="relative flex items-center gap-0.5 rounded-lg bg-neutral-25 px-0.5 w-max">
    <div class="relative flex items-center gap-1 pl-1.5 py-2 pr-2">
      <AdAccountIcon class="text-icon-normal transition-colors hover:text-icon-muted" 
      @mouseenter.native="showTooltip = true" @mouseleave.native="showTooltip = false" />
      <transition>
        <div v-if="showTooltip" class="ad-account-tooltip w-max">
          <BaseText type="label" size="xs" class="text-white px-1 py-0.5">
            Viewing ad account
          </BaseText>
        </div>
      </transition>
    </div>
    <!-- Selected Ad Account -->
    <div class="py-0.5 overflow-hidden">
      <div class="selected-ad-account flex items-center gap-1.5 p-1.5 rounded-md">
        <component :is="platformIcon.icon" class="text-icon-normal" :width="platformIcon.w" :height="platformIcon.h" />
        <BaseText type="label" size="sm" :class="selectedAdAccount ? 'text-text-muted' : 'text-text-normal'">
          {{ selectedAdAccount ? selectedAdAccount.name : 'None' }}
        </BaseText>
      </div>
    </div>
    <!-- Dropdown Opener -->
    <button v-if="showDropdownOpener" class="w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-100"
    :class="expanded ? 'bg-neutral-50' : 'hover:bg-neutral-50'"
    @click="expanded = !expanded">
      <ChevronIcon className="text-icon-normal transition-transform duration-100" :style="{ transform: expanded ? 'scaleY(-1)' : '' }" />
    </button>
    <!-- Dropdown Content -->
    <transition>
      <div v-if="expanded" class="dropdown" v-on-clickaway="() => { expanded = false }">
        <div class="dropdown-scroll flex flex-col gap-1">
          <button v-for="adAccount in adAccountOptions" :key="`ad-acct-opt-${statusData.lens_id}-${adAccount.id}`"
          class="flex items-center gap-2 p-1.5 rounded-md transition-colors hover:bg-neutral-25 min-w-0"
          @click="selectAdAccount(adAccount)">
            <component :is="platformIcon.icon" :width="16" :height="16" class="flex-shrink-0" />
            <BaseText type="label" size="sm" class="text-text-muted truncate">
              {{ adAccount.name }}
            </BaseText>
            <BaseText type="body" size="xs" class="text-text-subdued">
              {{ adAccount.id.replace('act_', '') }}
            </BaseText>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { reverseProviderKeyMap } from '../../../utils/lens/dataSourceUtils'
import { mixin as clickaway } from 'vue-clickaway2'

// Icons
import AdAccountIcon from '../../globals/Icons/AdAccountIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import MetaPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoTiktokIcon from '../../globals/Icons/socials/mono/MonoTiktokIcon.vue'

export default {
  name: 'OverviewAccountSelector',
  mixins: [clickaway],
  components: {
    AdAccountIcon,
    ChevronIcon
  },
  props: {
    statusData: {
      type: Object,
      default: () => {}
    },
    platform: {
      type: String,
      default: 'meta'
    },
    selectedAccountId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      expanded: false,
      showTooltip: false
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getProviders']),
    connectedAdAccounts () {
      if (!this.getProviders) return []
      return this.statusData.ad_accounts.map(account => {
        const { ad_account_id: id, ad_account_name: name, provider, is_valid: connected } = account
        return { 
          id, 
          name, 
          platform: reverseProviderKeyMap[provider], 
          connected 
        }
      })
    },
    adAccountOptions () {
      return this.connectedAdAccounts.filter(account => account.platform === this.platform && account.id !== this.selectedAccountId)
    },
    selectedAdAccount () {
      return this.selectedAccountId ? this.connectedAdAccounts.find(account => account.id === this.selectedAccountId) : null
    },
    platformIcon () {
      return this.selectedAccountId
        ? this.platform === 'meta' ? {icon: MetaPlatformIcon, w: 16, h: 16} : {icon: TikTokPlatformIcon, w: 16, h: 16}
        : this.platform === 'meta' ? {icon: MonoMetaIcon, w: 18, h: 18} : {icon: MonoTiktokIcon, w: 18, h: 18}
    },
    showDropdownOpener () {
      return this.adAccountOptions.length
    }
  },
  methods: {
    selectAdAccount (adAccount) {
      this.$emit('select', adAccount)
      this.expanded = false
    }
  }
}
</script>

<style scoped>
.selected-ad-account {
  background-color: white;
  box-shadow: 0 36px 48px -12px rgba(53, 57, 75, 0.20), 0 24px 32px -12px rgba(53, 57, 75, 0.24), 0 1px 2px 0 rgba(162, 172, 186, 0.16);
}
.dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  max-width: 428px;
  border-radius: 10px;
  background-color: white;
  box-shadow: 0 24px 32px -12px rgba(53, 57, 75, 0.12), 0 1px 2px 0 rgba(162, 172, 186, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.04), 0 1px 2px 0 rgba(162, 172, 186, 0.16), 0 0 0 1px #EBEFF3;
  z-index: 200;
  overflow: hidden;
}
.dropdown-scroll {
  overflow-y: auto;
  max-height: 220px;
  padding: 2px;
}
.dropdown-scroll::-webkit-scrollbar {
  width: 3px;
}
.dropdown-scroll::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}
.ad-account-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 8px;
  background: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(6px);
  padding: 2px;
  z-index: 1000;
  pointer-events: none;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>